// Pure helpers shared by the plans overview, the builder and the tenant page.

export const CATEGORY_ORDER = ['Catalog', 'Team', 'Sales', 'Marketing', 'Insights', 'Company'];

export const formatPrice = (amount, currency = 'USD', locale) => {
  try {
    return new Intl.NumberFormat(locale, { style: 'currency', currency, minimumFractionDigits: 0, maximumFractionDigits: 2 })
      .format((amount || 0) / 100);
  } catch (_) {
    return `${((amount || 0) / 100).toFixed(2)} ${currency}`;
  }
};

export const groupByCategory = (features) => {
  const groups = new Map();
  (features || []).forEach((f) => {
    if (!groups.has(f.category)) groups.set(f.category, []);
    groups.get(f.category).push(f);
  });
  return [...groups.entries()]
    .sort((a, b) => CATEGORY_ORDER.indexOf(a[0]) - CATEGORY_ORDER.indexOf(b[0]))
    .map(([category, items]) => ({ category, items }));
};

// One cell of the compare matrix / one bullet on a plan card.
// Returns { kind: 'yes'|'no'|'limit'|'unlimited', text }
export const describeEntitlement = (feature, entitlement) => {
  if (!entitlement || entitlement.enabled === false) return { kind: 'no', text: '—' };
  if (!feature || feature.kind === 'boolean') return { kind: 'yes', text: '✓' };
  if (entitlement.limit == null) return { kind: 'unlimited', text: '∞' };
  return { kind: 'limit', text: entitlement.limit.toLocaleString() };
};

export const emptyDraftFromPlan = (plan) => ({
  name: plan.name || '',
  description: plan.description || '',
  price: { amount: plan.price?.amount || 0, currency: plan.price?.currency || 'USD', interval: 'month' },
  highlight: Boolean(plan.highlight),
  entitlements: (plan.entitlements || []).map((e) => ({ featureKey: e.featureKey, enabled: e.enabled !== false, limit: e.limit ?? null })),
});

const fmt = (feature, e) => describeEntitlement(feature, e).text;

// Client-side change list — only used for the "N unsaved changes" count and
// the disabled state of Save. The server recomputes its own summary and that
// is what gets stored with the version.
export const diffDraft = (original, draft, featuresByKey) => {
  const changes = [];
  if (!original || !draft) return changes;
  if (original.name !== draft.name) changes.push('name');
  if ((original.description || '') !== (draft.description || '')) changes.push('description');
  if (original.price.amount !== draft.price.amount || original.price.currency !== draft.price.currency) changes.push('price');
  if (Boolean(original.highlight) !== Boolean(draft.highlight)) changes.push('highlight');
  const before = new Map(original.entitlements.map((e) => [e.featureKey, e]));
  const after = new Map(draft.entitlements.map((e) => [e.featureKey, e]));
  new Set([...before.keys(), ...after.keys()]).forEach((key) => {
    const f = featuresByKey[key];
    if (fmt(f, before.get(key)) !== fmt(f, after.get(key))) changes.push(key);
  });
  return changes;
};

export const validateDraft = (draft, t, featuresByKey) => {
  const errors = {};
  const name = (draft.name || '').trim();
  if (!name) errors.name = t('ga.validation.required');
  else if (name.length > 40) errors.name = t('At most 40 characters', { defaultValue: 'At most 40 characters' });
  if ((draft.description || '').length > 160) errors.description = t('At most 160 characters', { defaultValue: 'At most 160 characters' });
  if (!Number.isInteger(draft.price.amount) || draft.price.amount < 0) errors.price = t('Enter a price of 0 or more', { defaultValue: 'Enter a price of 0 or more' });
  draft.entitlements.forEach((e) => {
    const f = featuresByKey[e.featureKey];
    if (f && f.kind !== 'boolean' && e.limit != null && (!Number.isInteger(e.limit) || e.limit < 1)) {
      errors[`limit:${e.featureKey}`] = t('Whole number of at least 1, or unlimited', { defaultValue: 'Whole number of at least 1, or unlimited' });
    }
  });
  return errors;
};

// Dollars string ("69.00") <-> cents. Tolerates partial input while typing.
export const centsToInput = (cents) => ((cents || 0) / 100).toFixed(2);
export const inputToCents = (value) => {
  const n = Number.parseFloat(String(value).replace(/[^0-9.]/g, ''));
  if (Number.isNaN(n)) return 0;
  return Math.round(n * 100);
};

// Tone for an operator grant's expiry: 'critical' inside 7 days, 'warning'
// inside 30, 'ok' otherwise or when there is no expiry at all.
export const grantExpiryTone = (validUntil) => {
  if (!validUntil) return 'ok';
  const days = (new Date(validUntil).getTime() - Date.now()) / 86400000;
  if (Number.isNaN(days)) return 'ok';
  if (days <= 7) return 'critical';
  if (days <= 30) return 'warning';
  return 'ok';
};

export const LIB_PREFIX = 'lib:';
export const CANVAS_ID = 'canvas';
export const LIBRARY_ID = 'library';
